import { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';

import { pluralise } from '@brainpool/utils';
import { Modal } from '@brainpool/components/molecules/Modal/Modal';
import { filterProjectsByOwnership, goToPage } from '../../../utils/commonFunctions';

import { useDeleteCase } from '../../../hooks/useDeleteCase';
import { ModalProps } from '../../../types/ModalTypes';
import ModalButtons from '../../ModalButtons/ModalButtons';
import { useNavigate } from 'react-router-dom';
import { Case } from '../../../types/Case';
import { useListGroups } from '../../../hooks/useListGroups';
import {
  UpdateGroupMembersInput,
  useUpdateGroupMembers,
} from '../../../hooks/useUpdateGroupMembers';
import { useReactiveVar } from '@apollo/client/react';
import { reactiveVars, UserDetailsProps } from '@brainpool/apollo-connect';
import { useListCases } from '../../../hooks/useListCases';

interface DeleteModalProps extends ModalProps {
  redirect?: boolean;
}

export function DeleteModal({ data, open, handleClose, redirect = false }: DeleteModalProps) {
  const navigate = useNavigate();
  const { listCases } = useListCases();
  const { deleteCase, data: deleteData, loading } = useDeleteCase();
  const currentUser = useReactiveVar<UserDetailsProps>(reactiveVars.userDetails);

  const {
    listGroups: listCaseGroups,
    data: caseGroupsData,
    loading: caseGroupsLoading,
  } = useListGroups({ useCache: true });

  const {
    updateGroupMembers,
    data: updateGroupMembersData,
    loading: updateGroupLoading,
  } = useUpdateGroupMembers();

  //state
  const [ownedProjects, setOwnedProjects] = useState<Partial<Case>[]>([]);
  const [nonOwnedProjects, setNonOwnedProjects] = useState<Partial<Case>[]>([]);
  const [ownedProjectIDs, setOwnedProjectIDs] = useState<string[]>([]);
  const [nonOwnedProjectIDs, setNonOwnedProjectIDs] = useState<{ case_id: string }[]>([]);
  const [updateGroups, setUpdateGroups] = useState<UpdateGroupMembersInput[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);

  useEffect(() => {
    if (data && data.length > 0) {
      const { ownedProjects, nonOwnedProjects, ownedProjectIDs } = filterProjectsByOwnership(data);
      setOwnedProjects(ownedProjects);
      setNonOwnedProjects(nonOwnedProjects);
      setOwnedProjectIDs(ownedProjectIDs);
      const caseIds = nonOwnedProjects.map(({ id }) => ({ case_id: id as string })) || [];
      setNonOwnedProjectIDs(caseIds);
    }
  }, [data]);

  useEffect(() => {
    if (nonOwnedProjectIDs.length > 0) {
      listCaseGroups(nonOwnedProjectIDs, 100);

      // Collect all groups where current user is a member across ALL non-owned projects
      const allUserGroups: UpdateGroupMembersInput[] = [];

      caseGroupsData?.listGroups?.forEach(groupConnection => {
        const userGroups = groupConnection.items?.filter(group =>
          group.members.some(
            member => member.id?.toLowerCase() === currentUser.userId?.toLowerCase()
          )
        );

        if (userGroups) {
          userGroups.forEach(({ id }) => {
            allUserGroups.push({
              id: id,
              members: [currentUser.userId],
              action: 'REMOVE',
            });
          });
        }
      });

      setUpdateGroups(allUserGroups);
    } else {
      setIsValid(true);
    }
  }, [nonOwnedProjectIDs, caseGroupsData]);

  useEffect(() => {
    if (updateGroups.length > 0 && nonOwnedProjectIDs.length > 0) {
      setIsValid(true);
    }
  }, [updateGroups]);

  if (!data || data.length === 0) {
    return null;
  }

  const handleRemoveFromGroup = async () => {
    if (updateGroups.length > 0) {
      await updateGroupMembers({
        groups: updateGroups,
        caseIds: nonOwnedProjectIDs,
      });
    }
  };

  const handleDeleteCase = async () => {
    await handleRemoveFromGroup();
    await deleteCase(ownedProjectIDs);
    await listCases();

    if (handleClose) {
      handleClose();
    }

    if (redirect) {
      goToPage('/projects', navigate);
    }
  };

  return (
    <Modal
      titleType="h3"
      title={`Delete ${pluralise('Project', ownedProjects.length)}`}
      open={open}
      onClose={handleClose}
      children={
        <>
          {data.length > 0 && (
            <Box sx={{ marginBottom: '1rem' }}>
              <Typography variant="body1">
                Are you sure you want to delete {pluralise('this', data.length)}{' '}
                {pluralise('project', data.length)}?
              </Typography>
              {data.map(({ id, details }) => (
                <Chip
                  key={id}
                  label={details?.name ?? id}
                  sx={{ margin: '0.5rem 0.25rem' }}
                  color="success"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          <Typography variant="body1">This action cannot be undone.</Typography>
        </>
      }
      actions={
        <ModalButtons
          onCancel={handleClose}
          onConfirm={async () => handleDeleteCase()}
          isValid={isValid}
          isLoading={loading}
        />
      }
    />
  );
}

export default DeleteModal;
